/**
 * The BANC cell_class vocabulary in Simplified Chinese.
 *
 * The keys are the exact annotation strings the served meta.json carries (the
 * `cell_classes` list tools/build_webneurons.py interns from the same table
 * launchpad/data/neuron_annotations.json ships in `tables.cell_class`), so the
 * Hatch pick table and the plate chips read as Chinese anatomy instead of the
 * English annotation. Descriptive anatomy names are translated; acronyms and
 * canonical Latin class names (Kenyon cells, pars intercerebralis) keep the
 * term the literature uses. Anything outside this glossary renders as the raw
 * annotation name - never an invented one. cell-classes.test.ts holds the
 * glossary to the shipped data.
 */

import type { Lang } from "./i18n";

/** cell_class (as annotated) -> the Simplified Chinese class label. */
export const CELL_CLASS_ZH: ReadonlyMap<string, string> = new Map(
  Object.entries({
    abdomen_motor_neuron: "腹部运动神经元",
    amacrine_medulla: "髓质无长突细胞",
    antenna_motor_neuron: "触角运动神经元",
    antennal_lobe_centrifugal_neuron: "触角叶离心神经元",
    antennal_lobe_local_neuron: "触角叶局部神经元",
    antennal_lobe_output_neuron: "触角叶输出神经元",
    antennal_lobe_projection_neuron: "触角叶投射神经元",
    aorta_neuron: "主动脉神经元",
    aorta_sensory_neuron: "主动脉感觉神经元",
    ascending_neuron: "上行神经元",
    astrocyte: "星形胶质细胞",
    bristle_neuron: "刚毛神经元",
    campaniform_sensillum_neuron: "钟形感器神经元",
    central_complex_input_neuron: "中央复合体输入神经元",
    central_complex_intrinsic_neuron: "中央复合体内在神经元",
    central_complex_output_neuron: "中央复合体输出神经元",
    centrifugal_lobula: "小叶离心细胞",
    centrifugal_lobula_lobula_plate: "小叶-小叶板离心细胞",
    centrifugal_lobula_lobula_plate_medulla: "髓质-小叶-小叶板离心细胞",
    centrifugal_lobula_medulla: "小叶-髓质离心细胞",
    centrifugal_lobula_plate: "小叶板离心细胞",
    centrifugal_medulla: "髓质离心细胞",
    centrifugal_medulla_lobula: "髓质-小叶离心细胞",
    centrifugal_medulla_lobula_lobula_plate: "髓质-小叶-小叶板离心细胞",
    chordotonal_organ_neuron: "弦音器神经元",
    circadian_neuron: "昼夜节律神经元",
    cortex_glia: "皮层胶质细胞",
    crop_motor_neuron: "嗉囊运动神经元",
    descending_neuron: "下行神经元",
    distal_medulla: "远端髓质细胞",
    distal_medulla_dorsal_rim_area: "远端髓质背缘区细胞",
    eye_motor_neuron: "眼运动神经元",
    hair_plate_neuron: "毛板神经元",
    haltere_motor_neuron: "平衡棒运动神经元",
    hind_leg_motor_neuron: "后足运动神经元",
    hygrosensory_receptor_neuron: "湿觉感受器神经元",
    internal_sensillum_tactile_neuron: "内部感器-触觉神经元",
    internal_taste_sensillum_gustatory_neuron: "内部味觉感器-味觉神经元",
    kenyon_cell: "凯尼恩细胞",
    lamina_monopolar: "薄板单极细胞",
    lamina_tangential: "薄板切向细胞",
    lamina_wide_field: "薄板广域细胞",
    lateral_horn_centrifugal_neuron: "侧角离心神经元",
    lateral_horn_local_neuron: "侧角局部神经元",
    lateral_horn_output_neuron: "侧角输出神经元",
    leg_motor_neuron: "足运动神经元",
    lobula_columnar: "小叶柱状细胞",
    lobula_intrinsic: "小叶内在细胞",
    lobula_lobula_plate_columnar: "小叶-小叶板柱状细胞",
    lobula_lobula_plate_tangential: "小叶-小叶板切向细胞",
    lobula_medulla_amacrine: "小叶-髓质无长突细胞",
    lobula_medulla_tangential: "小叶-髓质切向细胞",
    lobula_plate_columnar: "小叶板柱状细胞",
    lobula_plate_intrinsic: "小叶板内在细胞",
    lobula_plate_lobula_columnar: "小叶板-小叶柱状细胞",
    lobula_plate_tangential_cell: "小叶板切向细胞",
    lobula_tangential: "小叶切向细胞",
    medial_antennal_lobula: "内侧触角叶小叶细胞",
    medulla_intrinsic: "髓质内在细胞",
    medulla_lobula_lobula_plate_amacrine: "髓质-小叶-小叶板无长突细胞",
    medulla_lobula_plate: "髓质-小叶板细胞",
    medulla_lobula_tangential: "髓质-小叶切向细胞",
    medulla_medulla: "髓质-髓质细胞",
    medulla_tangential: "髓质切向细胞",
    medulla_tubercle: "髓质结节细胞",
    multidendritic_neuron: "多树突神经元",
    mushroom_body_dopaminergic_neuron: "蘑菇体多巴胺能神经元",
    mushroom_body_extrinsic_neuron: "蘑菇体外部神经元",
    mushroom_body_output_neuron: "蘑菇体输出神经元",
    neck_motor_neuron: "颈部运动神经元",
    nervous_system_ensheathing_glia: "神经系统包裹胶质细胞",
    nutrient_sensory_neuron: "营养感觉神经元",
    ocellar_centrifugal_neuron: "单眼离心神经元",
    ocellar_intrinsic_neuron: "单眼内在神经元",
    ocellar_projection_neuron: "单眼投射神经元",
    olfactory_receptor_neuron: "嗅觉感受器神经元",
    optic_anterior: "前视区细胞",
    optic_lobe_intrinsic_centrifugal: "视叶内在离心细胞",
    orphan_neuron: "孤儿神经元",
    oxygenation_neuron: "氧合神经元",
    pars_intercerebralis_neurosecretory_cell: "脑间部神经分泌细胞",
    pars_lateralis_neurosecretory_cell: "侧部神经分泌细胞",
    peripheral_intrinsic_neuron: "外周内在神经元",
    pharynx_motor_neuron: "咽运动神经元",
    photoreceptor_neuron: "光感受器神经元",
    proboscis_motor_neuron: "喙运动神经元",
    proximal_distal_medulla_tangential: "近端-远端髓质切向细胞",
    proximal_medulla: "近端髓质细胞",
    reproductive_tract_sensory_neuron: "生殖道感觉神经元",
    salivary_motor_neuron: "唾液运动神经元",
    sequential_leg_neuromeres: "序列足神经节段",
    serpentine_medulla: "蛇形髓质细胞",
    single_leg_neuromere: "单足神经节段",
    spiracle_motor_neuron: "气门运动神经元",
    strand_neuron: "索状神经元",
    subesophageal_zone_neurosecretory_cell: "食管下区神经分泌细胞",
    subesophageal_zone_projection_neuron: "食管下区投射神经元",
    taste_bristle_gustatory_neuron: "味觉刚毛-味觉神经元",
    taste_bristle_neuron: "味觉刚毛神经元",
    taste_bristle_tactile_neuron: "味觉刚毛-触觉神经元",
    taste_peg_gustatory_neuron: "味觉栓-味觉神经元",
    taste_peg_tactile_neuron: "味觉栓-触觉神经元",
    thermosensory_receptor_neuron: "温觉感受器神经元",
    thoracic_abdominal_segmental_sensory_neuron: "胸腹节段感觉神经元",
    translobula_plate: "跨小叶板细胞",
    transmedullary: "跨髓质细胞",
    transmedullary_y: "跨髓质 Y 细胞",
    transverse_neuron: "横向神经元",
    tubercular_bulbar_neuron: "结节球神经元",
    unknown: "未知",
    unknown_motor_neuron: "未知运动神经元",
    uterus_motor_neuron: "子宫运动神经元",
    ventral_nerve_cord_centrifugal: "腹神经索离心细胞",
    ventral_nerve_cord_centripetal: "腹神经索向心细胞",
    ventral_nerve_cord_dorsal: "腹神经索背侧细胞",
    ventral_nerve_cord_neurosecretory_cell: "腹神经索神经分泌细胞",
    ventral_nerve_cord_serially_convergent: "腹神经索序列汇聚细胞",
    wedge_projection_neuron: "楔形投射神经元",
    wing_motor_neuron: "翅运动神经元",
    y_neuron: "Y 神经元",
  }),
);

/** The class label: the glossary in Chinese, the raw annotated name otherwise. */
export function cellClassLabel(lang: Lang, cellClass: string): string {
  const label = lang === "zh" ? CELL_CLASS_ZH.get(cellClass) : undefined;

  if (label !== undefined) return label;

  const words = cellClass.replaceAll("_", " ");

  return words.charAt(0).toUpperCase() + words.slice(1);
}
